import { useState, useEffect, useRef } from 'react';

interface LoadingScreenProps {
  onComplete: () => void;
}

export default function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [done, setDone] = useState(false);
  const animRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    const img = new Image();
    img.src = 'https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png';
    img.onload = () => { (window as any).__pixelMonsterLogo = img; };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;

    const TOTAL = 3000;
    startTimeRef.current = performance.now();

    // 大像素块参数
    const BLOCK_SIZE = 48;
    const COLS = Math.ceil(W / BLOCK_SIZE) + 1;
    const ROWS = Math.ceil(H / BLOCK_SIZE) + 1;

    const neonColors = ['#00ff41', '#00d9ff', '#ff006e', '#a855f7', '#ffcc00', '#ff4500'];
    const darkColors = ['#001a0a', '#00080f', '#0a0010', '#060010', '#0d0005'];

    // 每个像素块的状态
    interface Block {
      col: number;
      row: number;
      color: string;
      alpha: number;
      glitchOffset: number;
      glitchTimer: number;
      glitchInterval: number;
      phase: number; // 0~1 流动进度
      speed: number;
      lit: boolean;
    }

    const blocks: Block[] = [];
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const isNeon = Math.random() < 0.18;
        blocks.push({
          col: c,
          row: r,
          color: isNeon
            ? neonColors[Math.floor(Math.random() * neonColors.length)]
            : darkColors[Math.floor(Math.random() * darkColors.length)],
          alpha: Math.random() * 0.5 + 0.1,
          glitchOffset: 0,
          glitchTimer: Math.random() * 1000,
          glitchInterval: 200 + Math.random() * 600,
          phase: Math.random(),
          speed: 0.3 + Math.random() * 0.7,
          lit: false,
        });
      }
    }

    // 故障线条
    interface GlitchLine {
      y: number;
      width: number;
      x: number;
      color: string;
      alpha: number;
      life: number;
      maxLife: number;
    }
    const glitchLines: GlitchLine[] = [];

    const spawnGlitchLine = () => {
      glitchLines.push({
        y: Math.random() * H,
        width: 20 + Math.random() * 300,
        x: Math.random() * W,
        color: neonColors[Math.floor(Math.random() * neonColors.length)],
        alpha: 0.6 + Math.random() * 0.4,
        life: 0,
        maxLife: 80 + Math.random() * 120,
      });
    };

    // 扫描线
    let scanY = -H * 0.3;

    const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const draw = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const t = Math.min(elapsed / TOTAL, 1);

      ctx.clearRect(0, 0, W, H);

      // 背景
      ctx.fillStyle = '#060010';
      ctx.fillRect(0, 0, W, H);

      // ── Phase 1 (0~0.45): 大像素块从上往下流动涌入，带故障闪烁 ──
      // ── Phase 2 (0.45~0.75): 像素块聚焦中心，Logo浮现 ──
      // ── Phase 3 (0.75~1.0): 故障爆闪，整体淡出 ──

      const phase1End = 0.45;
      const phase2End = 0.75;

      // 流动像素块
      blocks.forEach((b) => {
        const bx = b.col * BLOCK_SIZE;
        const by = b.row * BLOCK_SIZE;

        let alpha = b.alpha;
        let drawX = bx;
        let drawY = by;
        let sz = BLOCK_SIZE - 1;
        let color = b.color;

        if (t < phase1End) {
          // 从屏幕外流入
          const localT = t / phase1End;
          const waveT = easeInOut(Math.min((localT - b.phase * 0.4) / 0.6, 1));
          drawY = by - H * (1 - Math.max(waveT, 0));
          alpha = b.alpha * Math.max(waveT, 0);

          // 故障偏移
          if (Math.random() < 0.003) {
            b.glitchOffset = (Math.random() - 0.5) * BLOCK_SIZE * 2;
          } else {
            b.glitchOffset *= 0.85;
          }
          drawX = bx + b.glitchOffset;

          // 随机点亮
          if (Math.random() < 0.002) {
            b.lit = true;
            b.color = neonColors[Math.floor(Math.random() * neonColors.length)];
          } else if (Math.random() < 0.01) {
            b.lit = false;
          }
          if (b.lit) {
            color = b.color;
            alpha = Math.min(alpha * 3, 1);
            sz = BLOCK_SIZE - 1;
          }
        } else if (t < phase2End) {
          // 向中心聚拢 + 部分块消失
          const localT = (t - phase1End) / (phase2End - phase1End);
          const cx2 = W / 2;
          const cy2 = H / 2;
          const pullStrength = easeInOut(localT) * 0.35;
          drawX = bx + (cx2 - bx) * pullStrength;
          drawY = by + (cy2 - by) * pullStrength;
          alpha = b.alpha * (1 - localT * 0.5);

          // 故障闪烁加剧
          if (Math.random() < 0.008) {
            b.glitchOffset = (Math.random() - 0.5) * BLOCK_SIZE * 3;
          } else {
            b.glitchOffset *= 0.7;
          }
          drawX += b.glitchOffset;

          if (b.lit || Math.random() < 0.004) {
            color = neonColors[Math.floor(Math.random() * neonColors.length)];
            alpha = Math.min(alpha * 4, 0.9);
          }
        } else {
          // 淡出阶段
          const localT = (t - phase2End) / (1 - phase2End);
          alpha = b.alpha * (1 - localT);
          if (Math.random() < 0.02) {
            b.glitchOffset = (Math.random() - 0.5) * BLOCK_SIZE * 5;
          } else {
            b.glitchOffset *= 0.6;
          }
          drawX = bx + b.glitchOffset;
          if (Math.random() < 0.01) {
            color = neonColors[Math.floor(Math.random() * neonColors.length)];
            alpha = Math.min(alpha * 5, 1);
          }
        }

        if (alpha <= 0.01) return;

        ctx.save();
        ctx.globalAlpha = Math.min(alpha, 1);
        ctx.fillStyle = color;

        // 霓虹块加发光
        const isNeonColor = neonColors.includes(color);
        if (isNeonColor && alpha > 0.3) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 18;
        }
        ctx.fillRect(drawX, drawY, sz, sz);
        ctx.restore();
      });

      // 故障线条
      if (t < phase2End && Math.random() < 0.12) spawnGlitchLine();

      for (let i = glitchLines.length - 1; i >= 0; i--) {
        const gl = glitchLines[i];
        gl.life++;
        const lifeT = gl.life / gl.maxLife;
        const lineAlpha = gl.alpha * Math.sin(lifeT * Math.PI);
        ctx.save();
        ctx.globalAlpha = lineAlpha;
        ctx.fillStyle = gl.color;
        ctx.shadowColor = gl.color;
        ctx.shadowBlur = 8;
        ctx.fillRect(gl.x, gl.y, gl.width, 2 + Math.random() * 3);
        // 双重故障线
        ctx.globalAlpha = lineAlpha * 0.4;
        ctx.fillRect(gl.x + (Math.random() - 0.5) * 20, gl.y + 4, gl.width * 0.6, 1);
        ctx.restore();
        if (gl.life >= gl.maxLife) glitchLines.splice(i, 1);
      }

      // 扫描线
      scanY += H * 0.008;
      if (scanY > H * 1.3) scanY = -H * 0.3;
      const scanGrad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      scanGrad.addColorStop(0, 'rgba(0,255,65,0)');
      scanGrad.addColorStop(0.5, 'rgba(0,255,65,0.06)');
      scanGrad.addColorStop(1, 'rgba(0,255,65,0)');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 60, W, 120);

      // 水平扫描细线
      ctx.save();
      ctx.globalAlpha = 0.15;
      ctx.strokeStyle = '#00ff41';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, scanY);
      ctx.lineTo(W, scanY);
      ctx.stroke();
      ctx.restore();

      // ── Logo 浮现 (phase2) ──
      if (t >= phase1End) {
        const logoT = Math.min((t - phase1End) / (phase2End - phase1End), 1);
        const logoAlpha = easeInOut(logoT) * (t >= phase2End ? Math.max(0, 1 - (t - phase2End) / (1 - phase2End) * 2) : 1);
        const logoImg = (window as any).__pixelMonsterLogo;

        const logoSize = Math.min(W, H) * 0.28;
        const cx = W / 2;
        const cy = H / 2;

        // Logo 光晕
        if (logoAlpha > 0.1) {
          const glowRadius = logoSize * 0.8 * logoAlpha;
          const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowRadius);
          glow.addColorStop(0, `rgba(0,217,255,${0.25 * logoAlpha})`);
          glow.addColorStop(0.5, `rgba(0,255,65,${0.12 * logoAlpha})`);
          glow.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = glow;
          ctx.fillRect(cx - glowRadius, cy - glowRadius, glowRadius * 2, glowRadius * 2);
        }

        if (logoImg && logoImg.complete && logoAlpha > 0.05) {
          // 故障偏移效果
          const glitchAmt = t < phase2End ? (1 - logoT) * 8 : 0;
          ctx.save();
          ctx.globalAlpha = logoAlpha * 0.35;
          ctx.fillStyle = '#ff006e';
          ctx.drawImage(logoImg, cx - logoSize / 2 + glitchAmt, cy - logoSize / 2, logoSize, logoSize);
          ctx.restore();

          ctx.save();
          ctx.globalAlpha = logoAlpha * 0.35;
          ctx.fillStyle = '#00d9ff';
          ctx.drawImage(logoImg, cx - logoSize / 2 - glitchAmt, cy - logoSize / 2, logoSize, logoSize);
          ctx.restore();

          ctx.save();
          ctx.globalAlpha = logoAlpha;
          ctx.drawImage(logoImg, cx - logoSize / 2, cy - logoSize / 2, logoSize, logoSize);
          ctx.restore();
        }

        // 品牌文字
        const textAlpha = logoAlpha;
        if (textAlpha > 0.05) {
          ctx.save();
          ctx.globalAlpha = textAlpha;
          ctx.textAlign = 'center';

          // 故障文字偏移
          const tGlitch = t < phase2End ? (1 - logoT) * 6 : 0;

          // 红色故障层
          ctx.globalAlpha = textAlpha * 0.5;
          ctx.font = 'bold 32px "Space Grotesk", monospace';
          ctx.fillStyle = '#ff006e';
          ctx.fillText('PIXEL MONSTER', cx + tGlitch, cy + logoSize / 2 + 44);

          // 蓝色故障层
          ctx.fillStyle = '#00d9ff';
          ctx.fillText('PIXEL MONSTER', cx - tGlitch, cy + logoSize / 2 + 44);

          // 主文字
          ctx.globalAlpha = textAlpha;
          const grad = ctx.createLinearGradient(cx - 160, 0, cx + 160, 0);
          grad.addColorStop(0, '#00ff41');
          grad.addColorStop(0.5, '#00d9ff');
          grad.addColorStop(1, '#ff006e');
          ctx.fillStyle = grad;
          ctx.shadowColor = '#00d9ff';
          ctx.shadowBlur = 24;
          ctx.fillText('PIXEL MONSTER', cx, cy + logoSize / 2 + 44);

          // 副标题
          ctx.globalAlpha = textAlpha * 0.7;
          ctx.font = '13px "JetBrains Mono", monospace';
          ctx.fillStyle = '#00ff41';
          ctx.shadowBlur = 8;
          ctx.fillText('AI VIDEO · PIXEL BEAST · RENDERING YOUR IMAGINATION', cx, cy + logoSize / 2 + 68);

          ctx.restore();
        }
      }

      // 最终淡出黑幕
      if (t >= phase2End) {
        const fadeT = (t - phase2End) / (1 - phase2End);
        ctx.save();
        ctx.globalAlpha = easeInOut(fadeT);
        ctx.fillStyle = '#060010';
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }

      if (t < 1) {
        animRef.current = requestAnimationFrame(draw);
      }
    };

    animRef.current = requestAnimationFrame(draw);

    const doneTimer = setTimeout(() => {
      setDone(true);
      onComplete();
    }, 3100);

    return () => {
      cancelAnimationFrame(animRef.current);
      clearTimeout(doneTimer);
    };
  }, [onComplete]);

  if (done) return null;

  return (
    <div
      className="fixed inset-0 z-[9999]"
      style={{ pointerEvents: 'all' }}
    >
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
